import styled, {css} from 'styled-components';

import KeyboardArrowRightIcon from '@edna/icons/keyboardArrowRight.svg';

import {Button as BaseButton, EButtonSize, TButtonSize} from '../Button';
import {Dropdown as BaseDropdown} from '../Dropdown';
import {mixins} from '../styles';
import {TPlacementX, TPlacementY} from '../types';

const isSmallSize = (size: TButtonSize) => ['XS', 'XXS'].includes(size as EButtonSize);
const getOptionHeight = (size: TButtonSize) => (isSmallSize(size) ? 24 : 36);

export const Button = styled(BaseButton)`
  display: inline-flex;
  white-space: nowrap;
`;

export const Dropdown = styled(BaseDropdown)`
  width: fit-content;
  height: fit-content;
`;

export const ArrowIcon = styled(KeyboardArrowRightIcon)<{size: TButtonSize}>`
  flex-shrink: 0;
  ${({size, theme}) => css`
    ${mixins.wh(isSmallSize(size) ? '12px' : '16px')};
    margin-left: ${isSmallSize(size) ? theme.spacing.s2 : theme.spacing.s4};
  `}
`;

type TOptionProps = {
  size: TButtonSize;
  isFirst?: boolean;
  isLast?: boolean;
  isDisabled?: boolean;
  isHovered?: boolean;
};

export const Option = styled.div<TOptionProps>`
  position: relative;
  display: flex;
  align-items: center;
  height: ${({size}) => getOptionHeight(size)}px;
  white-space: nowrap;
  padding: ${({theme}) => `${theme.spacing.s2} ${theme.spacing.s4}`};

  ${({size, theme}) => {
    if (isSmallSize(size)) {
      return css`
        ${mixins.f2};
        line-height: 12px;
        padding: ${theme.spacing.s1} ${theme.spacing.s3};
      `;
    }

    return css`
      ${mixins.f3};
      line-height: 14px;
      padding: ${theme.spacing.s1} ${theme.spacing.s4};
    `;
  }}

  ${({isDisabled, isHovered, theme}) => {
    if (isDisabled) {
      return css`
        color: ${theme.palette.grey300};

        &:hover {
          cursor: not-allowed;
        }
      `;
    }

    if (isHovered) {
      return css`
        color: ${theme.branding.content.primary};
        background-color: ${theme.branding.backgrounds.secondary};
        cursor: pointer;
      `;
    }
  }}

  ${({
    isFirst,
    isLast,
    theme: {
      borderRadius: {br1},
    },
  }) => {
    if (isFirst) {
      return css`
        border-radius: ${`${br1} ${br1} 0 0`};
      `;
    }

    if (isLast) {
      return css`
        border-radius: ${`0 0 ${br1} ${br1}`};
      `;
    }
  }}
`;

export const OptionContent = styled.div<{size: TButtonSize}>`
  width: 100%;
  ${mixins.stack({direction: 'ROW', align: 'center'})};

  svg {
    ${({size}) => mixins.wh(isSmallSize(size) ? '16px' : '20px')};
  }
`;

type TOverlayProps = {
  placementY: TPlacementY;
  placementX: TPlacementX;
  offsetX?: number;
};

export const NestedOverlay = styled.div<TOverlayProps>`
  ${mixins.paper};
  position: absolute;
  width: fit-content;

  ${({theme}) => css`
    box-shadow: ${theme.boxShadow.dropdown};
    background-color: ${theme.palette.white};
    z-index: ${theme.zIndex.dropdown};
  `};

  ${({placementY}) =>
    placementY === 'top'
      ? css`
          bottom: 0;
        `
      : css`
          top: 0;
        `}

  ${({placementX, offsetX = 0}) => {
    if (placementX === 'right' || placementX === 'center') {
      return css`
        left: ${offsetX}px;
        transform: translate(-100%, 0);
      `;
    }

    return css`
      left: calc(100% - ${offsetX}px);
    `;
  }}
`;
